import { useSelector } from 'react-redux';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Separated } from 'fp-ts/Separated';
import { ActionType, RouteName, TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { IconName, Stack } from '@optimus-web/core';
import { LocalizedSectionTitle, ModalWrapper, NewModalBodyWrapper, OverlayedLoader } from '@mews-commander/ui';
import { ApplicationState, WithEnterpriseId, WithGuideLink } from '@mews-commander/types';
import { Service, useRepositoryQuery } from '@mews-commander/data-access';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { orderOrderableEntityArray } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import { isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { ResponsiveTable } from '@mews-commander/ui/legacy';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { additionalServicesMapping, bookableServicesMapping } from './mappings';

export const ServiceList = ({ enterpriseId, guideLink }: WithEnterpriseId & WithGuideLink) => {
    const result = useRepositoryQuery(
        'services',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
        },
        {
            refetchOnMount: 'always',
            select: (data): Separated<Array<Service>, Array<Service>> =>
                pipe(
                    data.services,
                    array.filter((service) => service.isActive),
                    orderOrderableEntityArray,
                    array.partition(isBookableService)
                ),
        }
    );

    const enterpriseName = useSelector((state: ApplicationState) => getEnterpriseName(state, { enterpriseId }));

    const bookableServices = result.isSuccess ? result.data.right : [];

    const additionalServices = result.isSuccess ? result.data.left : [];

    const resolveRoute = useResolveRoute();
    const enterpriseUrl = resolveRoute(RouteName.EnterpriseDetail, { enterpriseId });
    const createBookableServiceUrl = resolveRoute(RouteName.VisitCreate, { query: { OwnerId: enterpriseId } });
    const createAdditionalServiceUrl = resolveRoute(RouteName.ProductServiceCreate, {
        query: { OwnerId: enterpriseId },
    });

    const breadcrumbs: Breadcrumbs = [
        {
            text: enterpriseName,
            url: enterpriseUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.ServicePlural} />,
            active: true,
        },
    ];

    const actions: HeadingAction[] = [
        {
            icon: IconName.Add,
            items: {
                createBookableService: {
                    icon: IconName.Add,
                    text: <LocalizedText textKey={TextKey.BookableService} />,
                    type: ActionType.Link,
                    url: createBookableServiceUrl,
                },
                additionalBookableService: {
                    icon: IconName.Add,
                    text: <LocalizedText textKey={TextKey.AdditionalService} />,
                    type: ActionType.Link,
                    url: createAdditionalServiceUrl,
                },
            },
            text: <LocalizedText textKey={TextKey.Create} />,
            type: ActionType.Dropdown,
        },
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ];

    return (
        <>
            <OverlayedLoader show={result.isFetching} />
            <ModalWrapper>
                <ModalHeader
                    title={<LocalizedText textKey={TextKey.BookableService} />}
                    actions={actions}
                    breadcrumbs={breadcrumbs}
                />
                <NewModalBodyWrapper padded>
                    <Stack spacing="400" vertical>
                        {array.isNonEmpty(bookableServices) ? (
                            <Stack spacing="200" vertical>
                                <LocalizedSectionTitle textKey={TextKey.BookableServicePlural} />
                                <ResponsiveTable mapping={bookableServicesMapping} source={bookableServices} />
                            </Stack>
                        ) : null}
                        {array.isNonEmpty(additionalServices) ? (
                            <Stack spacing="200" vertical>
                                <LocalizedSectionTitle textKey={TextKey.AdditionalServicePlural} />
                                <ResponsiveTable mapping={additionalServicesMapping} source={additionalServices} />
                            </Stack>
                        ) : null}
                    </Stack>
                </NewModalBodyWrapper>
            </ModalWrapper>
        </>
    );
};
